import { useBackgroundTasksChannel } from "@lib/hooks/useBackgroundTasksChannel";
import { useBackgroundTasksPolling } from "@lib/hooks/useBackgroundTasksPolling";
import {
  type BackgroundTask,
  selectActiveTasks,
  selectChildren,
  selectVisibleTasks,
  selectVisibleTopLevelTasks,
  useBackgroundTasksStore,
} from "@lib/stores/backgroundTasksStore";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { useRef, useState } from "react";
import { Link } from "react-router";

function subjectLink(task: BackgroundTask): string | null {
  if (!task.subjectType || !task.subjectId) return null;
  switch (task.subjectType) {
    case "Campaign":
      return `/campaigns/${task.subjectId}`;
    case "Candidate":
      return `/candidates/${task.subjectId}`;
    case "CampaignCandidate":
      return `/campaign-candidates/${task.subjectId}`;
    default:
      return null;
  }
}

function relativeTime(dateString: string | null): string {
  if (!dateString) return "";
  const seconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function StateBadge({ state }: { state: BackgroundTask["state"] }) {
  if (state === "running" || state === "queued") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-text-secondary">
        <Loader2 size={12} className="animate-spin" />
        Running
      </span>
    );
  }
  if (state === "succeeded") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
        <CheckCircle2 size={12} />
        Completed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
      <AlertCircle size={12} />
      Failed
    </span>
  );
}

export function BackgroundTasksIndicator() {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useBackgroundTasksChannel();
  useBackgroundTasksPolling();

  const tasksById = useBackgroundTasksStore((s) => s.tasksById);
  const topLevel = selectVisibleTopLevelTasks(tasksById);
  const allVisible = selectVisibleTasks(tasksById);
  const active = selectActiveTasks(tasksById);

  if (topLevel.length === 0) return null;

  const currentLabel = active[0]?.title ?? topLevel[0]?.title;
  const failedCount = allVisible.filter((t) => t.state === "failed").length;
  const showFailureTint = active.length === 0 && failedCount > 0;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-text-secondary transition-colors hover:bg-card-hover"
        title="Background tasks"
      >
        {active.length > 0 ? (
          <Loader2 size={14} className="animate-spin text-accent" />
        ) : showFailureTint ? (
          <AlertCircle size={14} className="text-red-600 dark:text-red-400" />
        ) : (
          <CheckCircle2 size={14} className="text-emerald-600 dark:text-emerald-400" />
        )}
        <span className="max-w-[14rem] truncate text-xs">{currentLabel}</span>
        {topLevel.length > 1 && (
          <span className="rounded-full bg-card-hover px-1.5 py-0.5 text-[10px] font-medium text-text-muted">
            {topLevel.length}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* biome-ignore lint/a11y/noStaticElementInteractions: backdrop overlay */}
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: backdrop overlay */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            ref={dropdownRef}
            className="absolute right-0 z-50 mt-2 w-96 overflow-hidden rounded-lg border border-border bg-card shadow-lg"
          >
            <div className="border-b border-border px-4 py-2">
              <h3 className="text-sm font-semibold text-text-primary">Background tasks</h3>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {topLevel.map((task) => {
                const href = subjectLink(task);
                const time = task.state === "running" ? task.startedAt : task.finishedAt;
                const children = selectChildren(tasksById, task.id);
                return (
                  <div key={task.id} className="border-b border-border px-4 py-3 last:border-b-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        {href ? (
                          <Link
                            to={href}
                            className="text-sm font-medium text-text-primary hover:text-accent"
                          >
                            {task.title}
                          </Link>
                        ) : (
                          <span className="text-sm font-medium text-text-primary">
                            {task.title}
                          </span>
                        )}
                        <div className="mt-0.5 flex items-center gap-2 text-xs text-text-faint">
                          <StateBadge state={task.state} />
                          {time && <span>&middot;</span>}
                          {time && <span>{relativeTime(time)}</span>}
                          {children.length > 0 && (
                            <>
                              <span>&middot;</span>
                              <span>
                                {children.filter((c) => c.state === "succeeded").length}/
                                {children.length} done
                              </span>
                            </>
                          )}
                        </div>
                        {task.state === "failed" && task.errorMessage && (
                          <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                            {task.errorMessage}
                          </p>
                        )}
                        {children.length > 0 && (
                          <ul className="mt-2 space-y-1">
                            {children.map((c) => (
                              <li
                                key={c.id}
                                className="flex items-center gap-2 text-xs text-text-secondary"
                              >
                                <StateBadge state={c.state} />
                                <span className="truncate">{c.title}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
